import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { fetchJobs } from '../api/jobs';
import type { Job, JobStatus, JobsPage } from '../api/jobs';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { DataTable } from '../components/data-table/DataTable';
import { ColumnsMenu, FilterBar, FilterRow, PageHeading, TablePagination } from '../components/data-table/TableChrome';
import { titleCase } from '../utils/format';
import { cn } from '../lib/utils';

// Tab order = the natural lifecycle of a job: queued → running → done.
// failed_terminal sits on the side as a problem queue (manual re-queue needed).
const TABS: Array<{ key: JobStatus | 'all'; label: string }> = [
  { key: 'all',             label: 'All' },
  { key: 'queued',          label: 'Queued' },
  { key: 'running',         label: 'Running' },
  { key: 'done',            label: 'Done' },
  { key: 'failed_terminal', label: 'Failed' },
  { key: 'cancelled',       label: 'Cancelled' },
];

const REFRESH_MS = 5_000;
const PAGE_SIZES = [50, 100, 250] as const;

const JOB_STATUS_VARIANT: Record<JobStatus, 'neutral' | 'success' | 'danger' | 'warning' | 'info'> = {
  queued:          'info',
  running:         'warning',
  done:            'success',
  failed:          'warning',
  failed_terminal: 'danger',
  cancelled:       'neutral',
};

const JOB_STATUS_DOT: Record<JobStatus, string> = {
  queued:          'bg-sky-500',
  running:         'bg-amber-500',
  done:            'bg-green-500',
  failed:          'bg-amber-600',
  failed_terminal: 'bg-red-600',
  cancelled:       'bg-zinc-400',
};

export default function QueuePage() {
  const [tab, setTab]         = useState<typeof TABS[number]['key']>('all');
  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [data, setData]       = useState<JobsPage | null>(null);
  const [error, setError]     = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  const load = useCallback(async (background = false) => {
    setError(null);
    try {
      const res = await fetchJobs({
        status: tab === 'all' ? undefined : tab,
        page,
        per_page: perPage,
      }, { background });
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [tab, page, perPage]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    // Auto-refresh is a timer, not the user (decision pollsession).
    const id = setInterval(() => { void load(true); }, REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  const columns = useMemo<ColumnDef<Job>[]>(() => [
    {
      id: 'id',
      size: 90,
      enableHiding: false,
      meta: { label: 'ID' },
      header: 'ID',
      cell: ({ row }) => <span className="font-mono text-[12.5px] text-muted-foreground">{row.original.id}</span>,
    },
    {
      id: 'type',
      meta: { label: 'Type' },
      header: 'Type',
      cell: ({ row }) => <span className="whitespace-nowrap font-mono text-[12.5px]">{row.original.job_type}</span>,
    },
    {
      id: 'status',
      meta: { label: 'Status' },
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={JOB_STATUS_VARIANT[row.original.status]} dot={JOB_STATUS_DOT[row.original.status]}>
          {titleCase(row.original.status)}
          {row.original.retry_count > 0 && <span className="opacity-75">×{row.original.retry_count}</span>}
        </Badge>
      ),
    },
    {
      id: 'payload',
      meta: { label: 'Payload' },
      header: 'Payload',
      cell: ({ row }) => (
        <div className="text-xs">
          <PayloadSummary payload={row.original.payload} />
          {row.original.last_error && (
            <details className="mt-1 cursor-pointer">
              <summary className="text-red-600">Error</summary>
              <pre className="mt-1 max-w-[420px] whitespace-pre-wrap rounded-md border border-red-200 bg-red-50 p-2 text-[11px] text-red-700">
                {row.original.last_error}
              </pre>
            </details>
          )}
        </div>
      ),
    },
    {
      id: 'created',
      meta: { label: 'Created' },
      header: 'Created',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">{relTime(row.original.created_at)}</span>
      ),
    },
    {
      id: 'duration',
      meta: { label: 'Duration' },
      header: 'Duration',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {duration(row.original.started_at, row.original.finished_at)}
        </span>
      ),
    },
    {
      id: 'by',
      meta: { label: 'By' },
      header: 'By',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">{row.original.created_by_email ?? '—'}</span>
      ),
    },
  ], []);

  const table = useReactTable({
    data: data?.items ?? [],
    columns,
    getRowId: row => String(row.id),
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    initialState: { columnPinning: { left: ['id'] } },
  });

  const stats      = data?.stats ?? {};
  const totalAll   = Object.values(stats).reduce<number>((acc, n) => acc + (n ?? 0), 0);
  const total      = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <>
      <PageHeading title="Job Queue" count={totalAll} />

      <FilterBar>
        <FilterRow>
          {/* Status tabs double as the filter for this view — counts make the
              backlog visible without opening each one. */}
          <div className="flex flex-wrap gap-1">
            {TABS.map(({ key, label }) => {
              const count = key === 'all' ? totalAll : (stats[key as JobStatus] ?? 0);
              const active = tab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setTab(key); setPage(1); }}
                  className={cn(
                    'flex h-8 items-center gap-2 rounded-md px-2.5 text-[13px] font-medium',
                    active ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                  )}
                >
                  {label}
                  <span
                    className={cn(
                      'rounded-sm px-1.5 py-0.5 text-[11px] tabular-nums',
                      active ? 'bg-background text-foreground' : 'bg-secondary text-muted-foreground',
                    )}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="ml-auto flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-[13px] text-muted-foreground">
              <Checkbox
                checked={autoRefresh}
                onCheckedChange={v => setAutoRefresh(!!v)}
                aria-label="Auto-refresh every 5 seconds"
              />
              Auto-refresh (5s)
            </label>
            <ColumnsMenu table={table} />
          </div>
        </FilterRow>
      </FilterBar>

      <DataTable
        table={table}
        loading={loading && !data}
        error={error}
        emptyMessage="No jobs."
      />

      <TablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        pageSizes={PAGE_SIZES}
        onPageChange={setPage}
        onPerPageChange={n => { setPerPage(n); setPage(1); }}
      />
    </>
  );
}

function PayloadSummary({ payload }: { payload: Record<string, unknown> }) {
  const keys = Object.keys(payload);
  if (keys.length === 0) return <span className="text-zinc-300">—</span>;
  return (
    <span className="font-mono text-zinc-700">
      {keys.slice(0, 3).map(k => `${k}=${shorten(payload[k])}`).join(' · ')}
      {keys.length > 3 && <span className="text-muted-foreground"> +{keys.length - 3}</span>}
    </span>
  );
}

function shorten(v: unknown): string {
  if (v === null || v === undefined) return '—';
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  return s.length > 30 ? s.slice(0, 30) + '…' : s;
}

function relTime(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  const diff = Math.max(0, (Date.now() - t) / 1000);
  if (diff < 60)    return `${Math.round(diff)}s ago`;
  if (diff < 3600)  return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function duration(started: string | null, finished: string | null): string {
  if (!started) return '—';
  const start = new Date(started).getTime();
  const end = finished ? new Date(finished).getTime() : Date.now();
  const ms = end - start;
  if (ms < 1000)   return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 60_000)}m`;
}
